/**
 * <TodoCard> — Claude Code-style live-updating checklist surfaced in chat
 * by `update_plan` / `lx_todo` tool calls. The card mutates in place as
 * the model re-calls the planning tool with an updated array.
 *
 * Post chat-content-model redesign: data flows inline via the
 * `tool_use` content block on the assistant message. <ToolUseBlock>
 * dispatches `update_plan` / `lx_todo` → <TodoCard todos runId>. No
 * store lookup; pure presentation. See ai-docs/lexiun-chat-content-
 * model-plan.md §3.
 *
 * Visual contract (preserved from lx-todo Phase 4):
 *   pending     — ☐ dim square + muted text
 *   in_progress — ◐ gold marker + bold + activeForm text + left accent stripe
 *   completed   — ✓ check + strikethrough + muted text
 *
 * Lexiun gold (gold-500/600) is the signature accent — used here for the
 * single in-progress item (gold-as-signature rule from the design
 * directives).
 *
 * Selectors are stable contract — do not change without updating
 * `lexide/tests/e2e/lx-todo-surfaces.spec.ts`.
 */
import { useTranslation } from "react-i18next";
import type { TodoItem } from "@/types/gateway";

interface TodoCardProps {
  todos: TodoItem[];
  runId: string;
}

export function TodoCard({ todos, runId }: TodoCardProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="todo-card"
      data-runid={runId}
      className="mb-3 rounded-md border border-cream-300 bg-cream-100/30 px-3 py-2 dark:border-coffee-700 dark:bg-coffee-900/30"
    >
      <div className="mb-2 text-caption font-medium uppercase tracking-wide text-coffee-600 dark:text-cream-400">
        {t("chat.todoCardTitle", "Tarefas")}
      </div>
      <ul className="flex flex-col gap-0.5">
        {todos.map((todo, idx) => (
          <TodoRow key={`${runId}-${idx}`} todo={todo} idx={idx} />
        ))}
      </ul>
    </div>
  );
}

function TodoRow({ todo, idx }: { todo: TodoItem; idx: number }) {
  // Marker per state (Phase 4 contract).
  const marker =
    todo.status === "completed" ? "✓" : todo.status === "in_progress" ? "◐" : "☐";

  // Row affordances per state:
  //   - in_progress: gold left accent stripe + soft tint + bold weight +
  //     gold marker. Shows `activeForm` (present-participle), not `content`.
  //   - completed: muted text + strikethrough + green check.
  //   - pending: muted text + dim square.
  const isInProgress = todo.status === "in_progress";
  const isCompleted = todo.status === "completed";

  const rowClass = [
    "flex items-start gap-2 pl-2 py-0.5 font-reading text-sm",
    isInProgress &&
      "border-l-2 border-gold-500 bg-gold-50/40 font-semibold text-coffee-900 dark:border-gold-400 dark:bg-gold-900/10 dark:text-cream-100",
    isCompleted &&
      "text-coffee-500 line-through dark:text-cream-500",
    !isInProgress &&
      !isCompleted &&
      "text-coffee-700 dark:text-cream-300",
  ]
    .filter(Boolean)
    .join(" ");

  const markerClass = isInProgress
    ? "text-gold-600 dark:text-gold-400"
    : isCompleted
      ? "text-coffee-500 dark:text-cream-500"
      : "text-coffee-500 dark:text-cream-500";

  const text = isInProgress ? todo.activeForm : todo.content;

  return (
    <li
      data-testid="todo-item"
      data-status={todo.status}
      data-idx={idx}
      className={rowClass}
    >
      <span aria-hidden className={`shrink-0 ${markerClass}`}>
        {marker}
      </span>
      <span
        data-testid={isInProgress ? "todo-item-activeform" : "todo-item-content"}
        className="min-w-0"
      >
        {text}
      </span>
    </li>
  );
}
